import { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import { LatLngExpression } from "leaflet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faPencil, faPlus, faTrash, faXmark, faEmptySet } from "@fortawesome/pro-solid-svg-icons";
import { v4 as uuidv4 } from 'uuid';

import { addMarker, groupedUndo, markersSelector, removeMarker, setSpaceMarker, SpaceType, useAppDispatch, useAppSelector, type MarkerType, spaceNumbersSelector, spaceLettersSelector } from "../../../store";

import { HTMLMapOverlay } from "../../HTMLMapOverlay";
import { Space } from "../../spaces/Map/Spaces/Space";
import { MarkerEditor } from "../MarkerEditor";

import { ResizableSpace } from "./ResizableSpace";
import { MarkerUploader } from "./MarkerUploader";
import { Icon } from "../Icon";

import './MarkerSelector.scss';

export function MarkerSelector({ space, position, onClose }: { space: SpaceType, position: LatLngExpression, onClose: () => void }) {
  const dispatch = useAppDispatch();
  const markers = useAppSelector(markersSelector);
  const number = useAppSelector(spaceNumbersSelector)[space.id];
  const letter = useAppSelector(spaceLettersSelector)[space.id];
  const currentMarker = markers.find((m) => m.id === space.markerId);

  const [editedMarkerId, setEditedMarkerId] = useState<MarkerType['id'] | undefined>(undefined);
  const [isNewMarker, setIsNewMarker] = useState(false);

  const map = useMap();
  useEffect(() => {
    const interactions = ['boxZoom', 'doubleClickZoom', 'dragging', 'keyboard', 'scrollWheelZoom', 'touchZoom'] as const;
    interactions.forEach((interaction) => map[interaction]?.disable());
    return () => interactions.forEach((interaction) => map[interaction]?.enable());
  }, [map]);

  if (editedMarkerId) {
    return (
      <>
        <MarkerEditor
          id={editedMarkerId}
          space={{ ...space, markerId: editedMarkerId }}
          isNewMarker={isNewMarker}
          onClose={() => {
            setEditedMarkerId(undefined);
            setIsNewMarker(false);
          }}
        />
        <Space space={space} position={position} />
      </>
    );
  }

  return (
    <>
      <HTMLMapOverlay
        latlng={position}
        anchor='bottom'
        fitInBounds={true}
      >
        <section
          className="marker-editing-overlay"
          // Avoid placing a new marker while clicking in "Add marker" mode
          onClick={(e) => e.stopPropagation()}
        >
          <ul role='listbox'>
            <li
              key='default'
              role='option'
              aria-selected={!currentMarker}
              onClick={() => {
                if (space.markerId !== 'default') {
                  dispatch(setSpaceMarker({ spaceId: space.id, markerId: 'default' }));
                }
                onClose();
              }}
              title="Use default marker"
            >
              <FontAwesomeIcon icon={ faEmptySet } size="3x" />
            </li>
            {markers.map((marker) => {
              return (
                <li
                  key={marker.id}
                  role='option'
                  aria-selected={marker.id === currentMarker?.id}
                  onClick={() => {
                    if (space.markerId !== marker.id) {
                      dispatch(setSpaceMarker({ spaceId: space.id, markerId: marker.id }));
                    }
                    onClose();
                  }}
                >
                  <Icon
                    marker={marker}
                    number={number}
                    letter={letter}
                  />
                  {marker.state && (
                    <>
                      <FontAwesomeIcon
                        icon={faPencil}
                        className='marker-edit-icon'
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditedMarkerId(marker.id);
                          setIsNewMarker(false);
                        }}
                      />
                      <FontAwesomeIcon
                        icon={faCopy}
                        className="marker-duplicate-icon"
                        title="Duplicate marker"
                        onClick={(e) => {
                          e.stopPropagation();
                          groupedUndo.batch(() => {
                            const newMarkerId = uuidv4();
                            const duplicatedMarker = {
                              ...marker,
                              id: newMarkerId,
                            };
                            dispatch(addMarker(duplicatedMarker));
                          });
                        }}
                      />
                    </>
                  )}
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="marker-delete-icon"
                    title="Delete marker"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(removeMarker(marker.id));
                    }}
                  />
                </li>
              );
            })}
            <MarkerUploader space={space} onUploadComplete={onClose} />
            <li
              role='option'
              aria-selected={false}
              title="Create new marker"
              onClick={() => {
                setEditedMarkerId(uuidv4());
                setIsNewMarker(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} size="3x" color="white" />
            </li>
            <li
              role='option'
              aria-selected={false}
              onClick={onClose}
              title="Close marker selector"
            >
              <FontAwesomeIcon icon={faXmark} size="3x" color="white" />
            </li>
          </ul>
        </section>
      </HTMLMapOverlay>
      <ResizableSpace
        space={space}
        position={position}
      />
    </>
  );
}
